import React, { useEffect, useState } from "react";
import axios from "axios";
import Navbar from "./Navbar";
import "../styles/InformesConfirmados.css";
import { generateInformePDFMejorado } from "../modules/inspeccion/components/GenerateReportsPDFMejorado";

// Normalizar API_URL (remover barra final si existe)
const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:4004').replace(/\/$/, '');

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

// Devuelve la fecha de referencia según el estado actual
const getFechaReferenciaPorEstado = (inf) => {
  if (!inf) return null;

  switch (inf.Estado) {
    case "confirmado":
    case "devuelto":
      return inf.FechaConfirmacion || inf.FechaRealizado;

    case "impreso":
      return inf.FechaImpresion || inf.FechaConfirmacion || inf.FechaRealizado;

    case "con_descargo":
      return inf.FechaDescargo || inf.FechaImpresion || inf.FechaRealizado;

    case "con_propuesta":
      return (
        inf.FechaSancionPropuesta ||
        inf.FechaDescargo ||
        inf.FechaImpresion ||
        inf.FechaRealizado
      );

    case "finalizado":
      return (
        inf.FechaFinalizado ||
        inf.FechaSancionPropuesta ||
        inf.FechaDescargo ||
        inf.FechaRealizado
      );

    case "aprobado":
      return inf.FechaAprobado || inf.FechaRealizado;

    default:
      return inf.FechaRealizado || null;
  }
};

// ¿Pasaron más de 5 días desde la fecha de referencia?
const estaVencido = (inf) => {
  const fechaRefStr = getFechaReferenciaPorEstado(inf);
  if (!fechaRefStr) return false;

  const fechaRef = new Date(fechaRefStr);
  if (isNaN(fechaRef.getTime())) return false;

  const hoy = new Date();
  const diffMs = hoy - fechaRef;
  const diffDias = diffMs / (1000 * 60 * 60 * 24);

  return diffDias > 5;
};

const InformesRRHH = () => {
  const [informes, setInformes] = useState([]);
  const [selectedInforme, setSelectedInforme] = useState(null);
  const [incluirFinalizados, setIncluirFinalizados] = useState(false);

  // Modales
  const [showDescargoModal, setShowDescargoModal] = useState(false);
  const [showPropuestaModal, setShowPropuestaModal] = useState(false);
  const [showAplicarModal, setShowAplicarModal] = useState(false);

  // Modal detalle
  const [showDetalle, setShowDetalle] = useState(false);
  const [detalleInforme, setDetalleInforme] = useState(null);

  // Campos controlados
  const [descargo, setDescargo] = useState("");
  const [sancionPropuesta, setSancionPropuesta] = useState("");
  const [sancionAplicada, setSancionAplicada] = useState("");

  useEffect(() => {
    const fetchInformes = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/informes/rrhh`, {
          params: { incluirFinalizados },
          headers: getAuthHeaders()
        });
        setInformes(data);
      } catch (error) {
        console.error("❌ Error al obtener informes RRHH:", error);
      }
    };
    fetchInformes();
  }, [incluirFinalizados]);

  // Imprimir informe
  const imprimirInforme = async (informe) => {
    try {
      await generateInformePDFMejorado({
        informeNumero: informe.NroInforme,
        lugarControl: informe.LugarControl,
        fecha: informe.FechaRealizado,
        hora: informe.Hora || "",
        actuante: informe.NombreInformante || informe.RealizadoPor,
        motivo: informe.Motivo,
        motivoDetalle: informe.MotivoDetalle,
        empleado: informe.NombreEmp || informe.LegajoEmp,
        legajo: informe.LegajoEmp,
        nroCoche: informe.CocheInterno,
        linea: informe.Linea,
        servicio: informe.Servicio,
        horaNotificacion: informe.HoraNotificacion,
        descargoTexto: informe.Descargo || "",
        fechaRespuesta: informe.FechaDescargo,
        firmaBase64: informe.FirmaBase64 || "",
      });

      // Solo cambiar estado a "impreso" si el informe está "confirmado"
      // Si ya está finalizado, solo generamos el PDF sin cambiar estado
      if (informe.Estado === "confirmado") {
        await axios.put(
          `${API_URL}/api/informes/${informe.id}/imprimir`,
          {},
          {
            headers: getAuthHeaders()
          }
        );

        setInformes((prev) =>
          prev.map((inf) =>
            inf.id === informe.id ? { ...inf, Estado: "impreso" } : inf
          )
        );
      }
    } catch (error) {
      console.error("❌ Error al imprimir informe:", error);
    }
  };

  // Cargar descargo
  const cargarDescargo = async (id, texto) => {
    try {
      await axios.put(
        `${API_URL}/api/informes/${id}/descargo`,
        { Descargo: texto },  // ✅ Usar el parámetro 'texto' en lugar del estado
        {
          headers: getAuthHeaders()
        }
      );
      setInformes((prev) =>
        prev.map((inf) =>
          inf.id === id
            ? { ...inf, Estado: "con_descargo", Descargo: texto }
            : inf
        )
      );
      setShowDescargoModal(false);
      setDescargo("");
    } catch (error) {
      console.error("❌ Error al cargar descargo:", error);
      alert(`Error al cargar descargo: ${error.response?.data?.error || error.message}`);
    }
  };

  // Cargar sanción propuesta
  const cargarPropuesta = async (id, sancion) => {
    try {
      await axios.put(
        `${API_URL}/api/informes/${id}/sancion-propuesta`,
        { SancionPropuesta: sancion },
        {
          headers: getAuthHeaders()
        }
      );
      setInformes((prev) =>
        prev.map((inf) =>
          inf.id === id
            ? { ...inf, Estado: "con_propuesta", SancionPropuesta: sancion }
            : inf
        )
      );
      setShowPropuestaModal(false);
      setSancionPropuesta("");
    } catch (error) {
      console.error("❌ Error al cargar sanción propuesta:", error);
    }
  };

  // Cargar sanción aplicada
  const aplicarSancion = async (id, sancion) => {
    try {
      await axios.put(
        `${API_URL}/api/informes/${id}/sancion-aplicada`,
        { SancionAplicada: sancion },
        {
          headers: getAuthHeaders()
        }
      );
      setInformes((prev) =>
        prev.map((inf) =>
          inf.id === id
            ? { ...inf, Estado: "finalizado", SancionAplicada: sancion }
            : inf
        )
      );
      setShowAplicarModal(false);
      setSancionAplicada("");
    } catch (error) {
      console.error("❌ Error al aplicar sanción:", error);
    }
  };

  // Abrir modales core
  const abrirModalDescargo = (inf) => {
    setSelectedInforme(inf);
    setShowDescargoModal(true);
  };
  const abrirModalPropuesta = (inf) => {
    setSelectedInforme(inf);
    setShowPropuestaModal(true);
  };
  const abrirModalAplicar = (inf) => {
    setSelectedInforme(inf);
    setShowAplicarModal(true);
  };

  // Abrir detalle
  const abrirDetalle = (inf) => {
    setDetalleInforme(inf);
    setShowDetalle(true);
  };

  return (
    <div className="informes-container">
      <Navbar />
      <h2 className="title">Informes RRHH</h2>

      <div className="mb-3">
        <label className="form-check-label">
          <input
            type="checkbox"
            className="form-check-input me-2"
            checked={incluirFinalizados}
            onChange={(e) => setIncluirFinalizados(e.target.checked)}
          />
          Mostrar informes finalizados
        </label>
      </div>

      <div className="table-wrapper">
        <table className="informes-table">
          <thead>
            <tr>
              <th>N° Informe</th>
              <th>Inspector</th>
              <th>Motivo</th>
              <th>Fecha</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {informes.map((inf) => {
              const vencido = estaVencido(inf);
              const fechaRef = getFechaReferenciaPorEstado(inf);

              return (
                <tr
                  key={inf.id}
                  className={vencido ? "fila-vencida" : ""}
                  title={
                    vencido && fechaRef
                      ? `Más de 5 días desde: ${new Date(
                          fechaRef
                        ).toLocaleString("es-AR")}`
                      : ""
                  }
                >
                  <td>{inf.NroInforme}</td>
                  <td>{inf.NombreInformante || 'Desconocido'}</td>
                  <td>{inf.Motivo}</td>
                  <td>
                    {inf.FechaRealizado
                      ? inf.FechaRealizado.split("T")[0]
                      : ""}
                  </td>
                  <td>
                    <span
                      className={
                        vencido ? "estado-label estado-vencido" : "estado-label"
                      }
                    >
                      {inf.Estado}
                      {vencido && <span className="estado-alerta"> ⚠</span>}
                    </span>
                  </td>
                  <td className="acciones">
                    <button
                      className="btn-detalle"
                      onClick={() => abrirDetalle(inf)}
                    >
                      📄 Detalle
                    </button>

                    {inf.Estado === "confirmado" && (
                      <button
                        className="btn-imprimir"
                        onClick={() => imprimirInforme(inf)}
                      >
                        🖨 Imprimir
                      </button>
                    )}

                    {inf.Estado === "impreso" && (
                      <button
                        className="btn-descargo"
                        onClick={() => abrirModalDescargo(inf)}
                      >
                        Cargar descargo
                      </button>
                    )}

                    {inf.Estado === "con_descargo" && (
                      <button
                        className="btn-propuesta"
                        onClick={() => abrirModalPropuesta(inf)}
                      >
                        Proponer sanción
                      </button>
                    )}

                    {inf.Estado === "con_propuesta" && (
                      <button
                        className="btn-aplicar"
                        onClick={() => abrirModalAplicar(inf)}
                      >
                        Registrar sanción
                      </button>
                    )}

                    {inf.Estado === "finalizado" && (
                      <>
                        <button
                          className="btn-imprimir"
                          onClick={() => imprimirInforme(inf)}
                          title="Imprimir informe finalizado"
                        >
                          🖨 Imprimir
                        </button>
                        <span className="badge-finalizado">✔ Finalizado</span>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal Descargo */}
      {showDescargoModal && selectedInforme && (
        <div
          className="modal-overlay"
          onClick={() => setShowDescargoModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>
              Cargar descargo para informe #{selectedInforme.NroInforme}
            </h3>
            <p>
              <strong>Motivo:</strong> {selectedInforme.Motivo}
            </p>
            <textarea
              value={descargo}
              onChange={(e) => setDescargo(e.target.value)}
              rows={4}
              style={{ width: "100%" }}
              placeholder="Escriba el descargo del empleado..."
            />
            <div className="modal-footer">
              <button
                onClick={() => setShowDescargoModal(false)}
                className="btn btn-ghost"
              >
                Cancelar
              </button>
              <button
                onClick={() => cargarDescargo(selectedInforme.id, descargo)}
                className="btn btn-primary"
                disabled={!descargo.trim()}
              >
                Guardar descargo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Propuesta */}
      {showPropuestaModal && selectedInforme && (
        <div
          className="modal-overlay"
          onClick={() => setShowPropuestaModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>
              Proponer sanción para informe #{selectedInforme.NroInforme}
            </h3>
            <p>
              <strong>Motivo:</strong> {selectedInforme.Motivo}
            </p>
            {selectedInforme.Descargo && (
              <p>
                <strong>Descargo:</strong> {selectedInforme.Descargo}
              </p>
            )}
            <textarea
              value={sancionPropuesta}
              onChange={(e) => setSancionPropuesta(e.target.value)}
              rows={4}
              style={{ width: "100%" }}
              placeholder="Escriba la sanción propuesta..."
            />
            <div className="modal-footer">
              <button
                onClick={() => setShowPropuestaModal(false)}
                className="btn btn-ghost"
              >
                Cancelar
              </button>
              <button
                onClick={() =>
                  cargarPropuesta(selectedInforme.id, sancionPropuesta)
                }
                className="btn btn-primary"
                disabled={!sancionPropuesta.trim()}
              >
                Guardar propuesta
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Aplicar sanción */}
      {showAplicarModal && selectedInforme && (
        <div
          className="modal-overlay"
          onClick={() => setShowAplicarModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>
              Aplicar sanción al informe #{selectedInforme.NroInforme}
            </h3>
            <p>
              <strong>Motivo:</strong> {selectedInforme.Motivo}
            </p>
            {selectedInforme.Descargo && (
              <p>
                <strong>Descargo:</strong> {selectedInforme.Descargo}
              </p>
            )}
            {selectedInforme.SancionPropuesta && (
              <p>
                <strong>Sanción propuesta:</strong>{" "}
                {selectedInforme.SancionPropuesta}
              </p>
            )}
            <textarea
              value={sancionAplicada}
              onChange={(e) => setSancionAplicada(e.target.value)}
              rows={4}
              style={{ width: "100%" }}
              placeholder="Escriba la sanción aplicada por la Junta..."
            />
            <div className="modal-footer">
              <button
                onClick={() => setShowAplicarModal(false)}
                className="btn btn-ghost"
              >
                Cancelar
              </button>
              <button
                onClick={() =>
                  aplicarSancion(selectedInforme.id, sancionAplicada)
                }
                className="btn btn-primary"
                disabled={!sancionAplicada.trim()}
              >
                Guardar sanción aplicada
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalle */}
      {showDetalle && detalleInforme && (
        <div
          className="modal-overlay"
          onClick={() => setShowDetalle(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Detalle del Informe #{detalleInforme.NroInforme}</h3>

            <div className="detalle-grid">
              <p>
                <strong>Inspector:</strong> {detalleInforme.NombreInformante || 'Desconocido'}
              </p>
              <p>
                <strong>Empleado:</strong> {detalleInforme.NombreEmp} (
                {detalleInforme.LegajoEmp})
              </p>
              <p>
                <strong>Fecha:</strong>{" "}
                {detalleInforme.FechaRealizado
                  ? detalleInforme.FechaRealizado.split("T")[0]
                  : ""}
              </p>
              <p>
                <strong>Hora:</strong> {detalleInforme.Hora}
              </p>
              <p>
                <strong>Motivo:</strong> {detalleInforme.Motivo}
              </p>
              <p>
                <strong>Detalle:</strong> {detalleInforme.MotivoDetalle}
              </p>
              <p>
                <strong>Servicio:</strong> {detalleInforme.Servicio}
              </p>
              <p>
                <strong>Línea:</strong> {detalleInforme.Linea}
              </p>
              <p>
                <strong>Coche:</strong> {detalleInforme.CocheInterno}
              </p>

              {detalleInforme.Descargo && (
                <p>
                  <strong>Descargo:</strong> {detalleInforme.Descargo}
                </p>
              )}
              {detalleInforme.SancionPropuesta && (
                <p>
                  <strong>Sanción propuesta:</strong>{" "}
                  {detalleInforme.SancionPropuesta}
                </p>
              )}
              {detalleInforme.SancionAplicada && (
                <p>
                  <strong>Sanción aplicada:</strong>{" "}
                  {detalleInforme.SancionAplicada}
                </p>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn-cerrar"
                onClick={() => setShowDetalle(false)}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InformesRRHH;
